import React, { useEffect, useRef } from 'react';
import { t as uiText } from '../../../i18n';
import { useEmailVerification } from '../EmailVerificationContext';
import { formatRemaining } from '../phase';
import ResendButton from './ResendButton';
import ChangeEmailButton from './ChangeEmailButton';
import EmailHelp from './EmailHelp';

const BANNER_VAR = '--email-banner-h';

/**
 * 24〜72時間の警告バナー。閉じられない。ヘッダー直下に固定し、
 * 高さを CSS 変数 --email-banner-h に反映してメインコンテンツ/サイドバーを押し下げる。
 */
const EmailVerificationBanner: React.FC = () => {
  const { phase, remainingMs, showPublicRag } = useEmailVerification();
  const ref = useRef<HTMLDivElement>(null);
  const visible = phase === 'B';

  useEffect(() => {
    const root = document.documentElement;
    if (!visible || !ref.current) {
      root.style.setProperty(BANNER_VAR, '0px');
      return;
    }
    const el = ref.current;
    const apply = () => root.style.setProperty(BANNER_VAR, `${el.offsetHeight}px`);
    apply();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    ro?.observe(el);
    window.addEventListener('resize', apply);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', apply);
      root.style.setProperty(BANNER_VAR, '0px');
    };
  }, [visible]);

  if (!visible) return null;
  const { hours, minutes } = formatRemaining(remainingMs);

  return (
    <div ref={ref} className="ev-banner" role="alert">
      <div className="ev-banner-text">
        <strong>{uiText('Your email address has not been verified yet')}</strong>
        <span>{uiText('Please complete email verification within 72 hours of creating your account.')}</span>
        <span>
          {showPublicRag
            ? uiText(
                'After the deadline, notreu features become unavailable. The public RAG may also stop under some conditions, and site visitors will not be able to use it.'
              )
            : uiText('After the deadline, notreu features become unavailable.')}
        </span>
        <span className="ev-banner-remaining">
          {uiText('Time remaining: {{hours}}h {{minutes}}m', { hours, minutes })}
        </span>
      </div>
      <div className="ev-banner-actions">
        <ResendButton />
        <ChangeEmailButton />
      </div>
      <EmailHelp />
    </div>
  );
};

export default EmailVerificationBanner;
